import { RefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import {
  intensityLevelLabels,
  SSMLTag,
  SSMLTagsColors,
} from "shared/constants";
import {
  handleSSMLValueClick,
  isEditorEmpty,
  useEditorSelection,
} from "shared/utils/tts-editor-helper";
import { useSSMLMenuDisabled } from "shared/utils/menu-disabling-rules";
import { getCurrentSSMLValue } from "features/TTS/TTSCustomEditor/utils/editorKeyDownHandler";
import { useIsActiveSSMLTag } from "shared/hooks/useSSMLTag";
import SSMLPreview from "features/TTS/TTSCustomEditor/components/SSMLPreview";
import CustomIconTextButtonMenu from "../../components/CustomIconTextButtonMenu";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import CustomSlider from "shared/components/CustomSlider";
import StyleListItemButton from "features/TTS/components/StyleListItemButton";
import { ListOfVoices } from "@generated/list_of_voices";
import { generateDynamicEmotions, getStylesForVoice } from "shared/utils/tts";
import {
  EmotionProps,
  getIconForStyle,
} from "shared/models/tts-editor/emotion";
import { useSetAtom } from "jotai";
import { styleEmotionNames } from "@/atoms";

interface StyleMenuProps {
  editorRef: RefObject<EditorRef | null>;
  onIntensityChange?: (value: string) => void;
  disabled?: boolean;
  listOfVoices?: ListOfVoices;
  selectedVoice?: string;
}

export default function StyleMenu({
  editorRef,
  onIntensityChange,
  disabled,
  listOfVoices,
  selectedVoice,
}: StyleMenuProps): JSX.Element {
  const { t } = useTranslation();
  // Set default intensity index as 2 for 'medium'
  const defaultIntensityIndex = 2;
  const [sliderValue, setSliderValue] = useState(defaultIntensityIndex);
  const isStyleTagActive = useIsActiveSSMLTag(SSMLTag.Style);
  // Set default style to 'neutral'
  const [selectedStyle, setSelectedStyle] = useState<string>("neutral");
  const editorEmpty = isEditorEmpty(editorRef);
  const hasSelection = useEditorSelection(editorRef);
  const setEmotionNamesAtom = useSetAtom(styleEmotionNames)

  const [availableEmotions, setAvailableEmotions] = useState<EmotionProps[]>(
    []
  );

  useEffect(() => {
    if (listOfVoices) {
      let emotions: EmotionProps[];

      if (selectedVoice) {
        const voiceStyles = getStylesForVoice(selectedVoice, listOfVoices);
        emotions = voiceStyles.map((styleName) => ({
          icon: getIconForStyle(styleName),
          name: styleName,
        }));
      } else {
        emotions = generateDynamicEmotions(listOfVoices);
      }

      setAvailableEmotions(emotions);
      setEmotionNamesAtom(prev => {
        const newNames = emotions.map(e => e.name);
        // Only update if names actually changed (this is to prevent infinite looping)
        if (JSON.stringify(prev) === JSON.stringify(newNames)) return prev;
        return newNames;
      });
      // Set default style to first available or "neutral"
      if (emotions.length > 0 && !selectedStyle) {
        const neutralStyle = emotions.find((e) => e.name === "neutral");
        setSelectedStyle(neutralStyle?.name || emotions[0].name);
      }
    } else {
      setSelectedStyle("neutral");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOfVoices, selectedVoice]);

  const hasChanges = isStyleTagActive
    ? selectedStyle !==
        getCurrentSSMLValue(editorRef, SSMLTag.Style)?.split(", ")[0] ||
      intensityLevelLabels[sliderValue] !==
        getCurrentSSMLValue(editorRef, SSMLTag.Style)?.split(", ")[1]
    : !!selectedStyle;

  const onStyleSelection = (selectedStyleName: string) => {
    setSelectedStyle(selectedStyleName);
  };

  useEffect(() => {
    if (isStyleTagActive && editorRef.current?.editor) {
      const currentValue = getCurrentSSMLValue(editorRef, SSMLTag.Style);
      if (currentValue) {
        const [styleName, styleIntensity] = currentValue.split(", ");
        setSelectedStyle(styleName);

        // Find the index of the intensity level to set the slider
        const intensityIndex = intensityLevelLabels.indexOf(styleIntensity);
        if (intensityIndex !== -1) {
          setSliderValue(intensityIndex);
        }
      }
    }
  }, [isStyleTagActive, editorRef]);

  const handleStyleSelection = () => {
    if (!editorRef) return;

    handleSSMLValueClick(
      editorRef,
      SSMLTag.Style,
      `${selectedStyle}, ${intensityLevelLabels[sliderValue]}`,
      SSMLTagsColors.StyleTag,
      [SSMLTag.Style]
    );
  };

  const onDialogCloseHandler = () => {
    if (!isStyleTagActive) {
      const defaultStyle = availableEmotions.find((e) => e.name === "neutral");
      setSelectedStyle(
        defaultStyle?.name || availableEmotions[0]?.name || "neutral"
      );
      setSliderValue(defaultIntensityIndex);
    }
  };

  const onDialogOpenHandler = () => {
    if (isStyleTagActive && editorRef.current?.editor) {
      const currentValue = getCurrentSSMLValue(editorRef, SSMLTag.Style);
      if (currentValue) {
        const [styleName, styleIntensity] = currentValue.split(", ");
        setSelectedStyle(styleName);

        const intensityIndex = intensityLevelLabels.indexOf(styleIntensity);
        if (intensityIndex !== -1) {
          setSliderValue(intensityIndex);
        }
      }
    } else {
      const defaultStyle = availableEmotions.find((e) => e.name === "neutral");
      setSelectedStyle(
        defaultStyle?.name || availableEmotions[0]?.name || "neutral"
      );
      setSliderValue(defaultIntensityIndex);
    }
  };

  const handleSliderChange = (value: number) => {
    setSliderValue(value);
    if (onIntensityChange) {
      onIntensityChange(intensityLevelLabels[value]);
    }
  };

  const isSelectionDisabled = useSSMLMenuDisabled(editorRef, SSMLTag.Style);

  const isDisabled =
    disabled ||
    editorEmpty ||
    isSelectionDisabled ||
    (!hasSelection && !isStyleTagActive);

  return (
    <CustomIconTextButtonMenu
      useDialog
      componentId="style"
      icon={InsertEmoticonIcon}
      menuTitle={t("tts.toolbarMenu.styleMenu.title")}
      dialogHeader={t("tts.toolbarMenu.styleMenu.title")}
      isActive={isStyleTagActive}
      onDialogPrimaryButtonClick={handleStyleSelection}
      isDialogPrimaryButtonDisabled={
        isStyleTagActive ? !hasChanges : !selectedStyle
      }
      shouldBeDisabled={isDisabled}
      onDialogClose={onDialogCloseHandler}
      onDialogOpen={onDialogOpenHandler}
      isEditorEmpty={editorEmpty}
      editorRef={editorRef}
      tagType={SSMLTag.Style}
      showClearButton={isStyleTagActive}
      isSelectionDisabled={isSelectionDisabled}
      footerText={
        <SSMLPreview
          type="style"
          styleAttributes={{
            name: selectedStyle,
            intensity: intensityLevelLabels[sliderValue],
          }}
        />
      }
    >
      <Grid container spacing={2}>
        {availableEmotions.map((emotion) => (
          <Grid item xs={12} md={6} lg={4} key={emotion.name}>
            <StyleListItemButton
              id={`style-${emotion.name}`}
              emotion={emotion}
              isSelected={selectedStyle === emotion.name}
              onSelect={() => onStyleSelection(emotion.name)}
            />
          </Grid>
        ))}
      </Grid>
      <Stack direction="row" justifyContent="space-between" paddingY={4}>
        <Typography variant="subtitle1">
          {t("tts.toolbarMenu.styleMenu.intensity")}
        </Typography>
      </Stack>
      <Box
        sx={{
          ml: 4,
          mt: 2,
          mb: 2,
          width: "92%",
        }}
      >
        <CustomSlider
          value={sliderValue}
          defaultValue={defaultIntensityIndex}
          step={1}
          minLabel="x-weak"
          maxLabel="x-strong"
          labels={intensityLevelLabels}
          onSliderChange={handleSliderChange}
          showCheckButton={false}
          isVolumeMenu
          showAllLabels={true}
          id="style-intensity-slider"
        />
      </Box>
    </CustomIconTextButtonMenu>
  );
}
